import React, { useState, useEffect } from 'react';
import { Icon } from '../../utils/icons';
import OAuthResultModal from '../OAuthResultModal';
import ConfirmationModal from '../ConfirmationModal';
import api from '../../services/api';

const AppointmentIntegrations = ({ config, onConfigChange }) => {
    const integrations = config.integrations || {};

    // Add custom animations
    const modalAnimations = `
        .animate-fadeIn {
            animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-scaleIn {
            animation: scaleIn 0.2s ease-out forwards;
        }
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
    `;

    // Inject styles
    React.useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = modalAnimations;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    // State for Google Calendar integration
    const [googleCalendarState, setGoogleCalendarState] = useState({
        isConnected: false,
        isLoading: false,
        connectionStatus: 'disconnected',
        googleAccount: null,
        availableCalendars: [],
        settings: {
            defaultCalendarId: 'primary',
            createEvents: true,
            sendInvitations: true,
            eventVisibility: 'default',
            reminders: {
                enabled: true,
                methods: [{ method: 'email', minutes: 1440 }]
            }
        },
        stats: null
    });

    // State for Google Sheets integration
    const [googleSheetsState, setGoogleSheetsState] = useState({
        isConnected: false,
        isLoading: false,
        connectionStatus: 'disconnected',
        googleAccount: null,
        availableSpreadsheets: [],
        settings: {
            defaultSpreadsheetId: null,
            defaultSpreadsheetName: null,
            worksheetName: 'Appointments',
            autoCreateSpreadsheet: true,
            syncNewAppointments: true,
            updateMode: 'append',
            includeCustomerInfo: true,
            includeServiceDetails: true,
            includeStatus: true,
            includeNotes: true,
            dateFormat: 'MM/DD/YYYY',
            timeFormat: '12'
        },
        stats: null
    });

    const [showCalendarSettings, setShowCalendarSettings] = useState(false);
    const [showSheetsSettings, setShowSheetsSettings] = useState(false);
    const [showAdvancedTracking, setShowAdvancedTracking] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    const googleCalendar = integrations.googleCalendar || { enabled: false };
    const googleSheets = integrations.googleSheets || { enabled: false };

    // Load integrations status on component mount
    useEffect(() => {
        loadGoogleCalendarStatus();
        loadGoogleSheetsStatus();
        checkForIntegrationCallback();

        // Check if we just returned from OAuth connection
        const wasConnectingCalendar = sessionStorage.getItem('googleCalendarConnecting');
        const wasConnectingSheets = sessionStorage.getItem('googleSheetsConnecting');

        if (wasConnectingCalendar) {
            // Give backend a moment to save the integration, then check status
            setTimeout(() => {
                loadGoogleCalendarStatus();
                sessionStorage.removeItem('googleCalendarConnecting');
            }, 1500);
        }

        if (wasConnectingSheets) {
            // Give backend a moment to save the integration, then check status
            setTimeout(() => {
                loadGoogleSheetsStatus();
                sessionStorage.removeItem('googleSheetsConnecting');
            }, 1500);
        }
    }, []);

    // Check for integration success/error in URL parameters
    const checkForIntegrationCallback = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const integrationSuccess = urlParams.get('integration_success');
        const integrationError = urlParams.get('integration_error');
        const errorDetails = urlParams.get('details');

        if (integrationSuccess === 'google_calendar') {
            // Clear connecting state
            sessionStorage.removeItem('googleCalendarConnecting');

            // Show success modal
            setSuccessModalData({
                title: 'Google Calendar Connected!',
                message: 'Your Google Calendar has been successfully connected. New appointments will automatically be added to your calendar.',
                type: 'success',
                service: 'google_calendar',
                accountInfo: googleCalendarState.googleAccount
            });
            setShowSuccessModal(true);

            // Clean up OAuth parameters while preserving original URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('integration_success');
            urlParams.delete('integration_error');
            urlParams.delete('details');

            const cleanedParams = urlParams.toString();
            const newUrl = window.location.pathname + (cleanedParams ? `?${cleanedParams}` : '');
            window.history.replaceState({}, document.title, newUrl);

            // Reload integration status to show connected state immediately
            setTimeout(() => {
                loadGoogleCalendarStatus();
            }, 500);
        } else if (integrationSuccess === 'google_sheets') {

            // Clear connecting state
            sessionStorage.removeItem('googleSheetsConnecting');

            // Show success modal
            setSuccessModalData({
                title: 'Google Sheets Connected!',
                message: 'Your Google Sheets has been successfully connected. New appointments will automatically be added to your spreadsheet.',
                type: 'success',
                service: 'google_sheets',
                accountInfo: googleSheetsState.googleAccount
            });
            setShowSuccessModal(true);

            // Clean up OAuth parameters while preserving original URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('integration_success');
            urlParams.delete('integration_error');
            urlParams.delete('details');

            const cleanedParams = urlParams.toString();
            const newUrl = window.location.pathname + (cleanedParams ? `?${cleanedParams}` : '');
            window.history.replaceState({}, document.title, newUrl);

            // Reload integration status to show connected state immediately
            setTimeout(() => {
                loadGoogleSheetsStatus();
            }, 500);
        } else if (integrationError) {
            // Show error modal
            const errorMessages = {
                'access_denied': 'Access was denied. Please try connecting again and grant the necessary permissions.',
                'no_code': 'No authorization code received from Google. Please try again.',
                'invalid_state': 'Invalid authentication state. Please try connecting again.',
                'missing_user_id': 'User authentication error. Please refresh the page and try again.',
                'redirect_mismatch': 'Configuration error. Please contact support.',
                'invalid_code': 'Authorization code expired or invalid. Please try connecting again.',
                'invalid_credentials': 'Invalid Google credentials. Please check the configuration.',
                'callback_failed': 'Connection failed. Please try again.'
            };

            const isCalendarError = integrationError && window.location.search.includes('google_calendar');
            const isSheetsError = integrationError && window.location.search.includes('google_sheets');

            setSuccessModalData({
                title: 'Connection Failed',
                message: errorMessages[integrationError] || errorDetails || 'An error occurred while connecting. Please try again.',
                type: 'error',
                service: isCalendarError ? 'google_calendar' : isSheetsError ? 'google_sheets' : null
            });
            setShowSuccessModal(true);

            // Clean up OAuth parameters while preserving original URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete('integration_success');
            urlParams.delete('integration_error');
            urlParams.delete('details');

            const cleanedParams = urlParams.toString();
            const newUrl = window.location.pathname + (cleanedParams ? `?${cleanedParams}` : '');
            window.history.replaceState({}, document.title, newUrl);
        }
    };

    const loadGoogleCalendarStatus = async () => {
        setGoogleCalendarState(prev => ({ ...prev, isLoading: true }));

        try {
            const response = await api.get('/integrations/google-calendar');

            if (response.data.success) {
                const integrationData = response.data.data;

                setGoogleCalendarState(prev => ({
                    ...prev,
                    isConnected: integrationData.isEnabled === true && integrationData.connectionStatus === 'connected',
                    connectionStatus: integrationData.connectionStatus || 'disconnected',
                    googleAccount: integrationData.googleAccount || null,
                    availableCalendars: integrationData.availableCalendars || [],
                    settings: {
                        ...prev.settings,
                        ...integrationData.calendarSettings
                    }
                }));
            }
        } catch (error) {
            console.error('Error loading Google Calendar status:', error);
            // Reset to disconnected state on error
            setGoogleCalendarState(prev => ({
                ...prev,
                isConnected: false,
                connectionStatus: 'disconnected',
                googleAccount: null,
                availableCalendars: []
            }));
        } finally {
            setGoogleCalendarState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleGoogleCalendarConnect = async () => {
        setGoogleCalendarState(prev => ({ ...prev, isLoading: true }));

        try {
            // Send current page URL as redirect destination
            const currentPath = window.location.pathname + window.location.search;

            const response = await api.post('/integrations/google-calendar/connect', {
                redirectUrl: currentPath
            });

            if (response.data.success && response.data.data.authUrl) {

                // Store connecting state in sessionStorage to detect when we return
                sessionStorage.setItem('googleCalendarConnecting', 'true');

                // Redirect to Google OAuth (same window)
                window.location.href = response.data.data.authUrl;
            } else {
                throw new Error('Failed to get authorization URL');
            }
        } catch (error) {
            console.error('❌ Error connecting to Google Calendar:', error);
            setSuccessModalData({
                title: 'Connection Failed',
                message: 'Failed to connect to Google Calendar. Please try again.',
                type: 'error',
                service: 'google_calendar'
            });
            setShowSuccessModal(true);
            setGoogleCalendarState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleGoogleCalendarDisconnect = async () => {
        setConfirmModalData({
            title: 'Disconnect Google Calendar',
            message: 'Are you sure you want to disconnect Google Calendar? This will stop syncing future appointments.',
            confirmText: 'Disconnect',
            type: 'danger',
            onConfirm: () => performGoogleCalendarDisconnect()
        });
        setShowConfirmModal(true);
    };

    const performGoogleCalendarDisconnect = async () => {

        setGoogleCalendarState(prev => ({ ...prev, isLoading: true }));

        try {
            const response = await api.delete('/integrations/google-calendar');

            if (response.data.success) {

                // Reset state immediately
                setGoogleCalendarState(prev => ({
                    ...prev,
                    isConnected: false,
                    connectionStatus: 'disconnected',
                    googleAccount: null,
                    availableCalendars: [],
                    settings: {
                        defaultCalendarId: 'primary',
                        createEvents: true,
                        sendInvitations: true,
                        eventVisibility: 'default',
                        reminders: {
                            enabled: true,
                            methods: []
                        },
                        twoWaySync: false
                    }
                }));

                // Reload status to ensure consistency
                setTimeout(() => {
                    loadGoogleCalendarStatus();
                }, 500);

            } else {
                throw new Error(response.data.message || 'Failed to disconnect');
            }
        } catch (error) {
            console.error('❌ Error disconnecting Google Calendar:', error);
            setSuccessModalData({
                title: 'Disconnection Failed',
                message: 'Failed to disconnect Google Calendar. Please try again.',
                type: 'error',
                service: 'google_calendar'
            });
            setShowSuccessModal(true);
        } finally {
            setGoogleCalendarState(prev => ({ ...prev, isLoading: false }));
        }
    };

    // Google Sheets integration methods
    const loadGoogleSheetsStatus = async () => {
        setGoogleSheetsState(prev => ({ ...prev, isLoading: true }));

        try {
            const response = await api.get('/integrations/google-sheets');

            if (response.data.success) {
                const integrationData = response.data.data;

                setGoogleSheetsState(prev => ({
                    ...prev,
                    isConnected: integrationData.isEnabled === true && integrationData.connectionStatus === 'connected',
                    connectionStatus: integrationData.connectionStatus || 'disconnected',
                    googleAccount: integrationData.googleAccount || null,
                    availableSpreadsheets: integrationData.availableSpreadsheets || [],
                    settings: {
                        ...prev.settings,
                        ...integrationData.sheetsSettings
                    }
                }));
            }
        } catch (error) {
            console.error('Error loading Google Sheets status:', error);
            // Reset to disconnected state on error
            setGoogleSheetsState(prev => ({
                ...prev,
                isConnected: false,
                connectionStatus: 'disconnected',
                googleAccount: null,
                availableSpreadsheets: []
            }));
        } finally {
            setGoogleSheetsState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleGoogleSheetsConnect = async () => {
        setGoogleSheetsState(prev => ({ ...prev, isLoading: true }));

        try {
            // Send current page URL as redirect destination
            const currentPath = window.location.pathname + window.location.search;

            const response = await api.post('/integrations/google-sheets/connect', {
                redirectUrl: currentPath
            });

            if (response.data.success && response.data.data.authUrl) {

                // Store connecting state in sessionStorage to detect when we return
                sessionStorage.setItem('googleSheetsConnecting', 'true');

                // Redirect to Google OAuth (same window)
                window.location.href = response.data.data.authUrl;
            } else {
                throw new Error('Failed to get authorization URL');
            }
        } catch (error) {
            console.error('❌ Error connecting to Google Sheets:', error);
            setSuccessModalData({
                title: 'Connection Failed',
                message: 'Failed to connect to Google Sheets. Please try again.',
                type: 'error',
                service: 'google_sheets'
            });
            setShowSuccessModal(true);
            setGoogleSheetsState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleGoogleSheetsDisconnect = async () => {
        setConfirmModalData({
            title: 'Disconnect Google Sheets',
            message: 'Are you sure you want to disconnect Google Sheets? This will stop syncing future appointments.',
            confirmText: 'Disconnect',
            type: 'danger',
            onConfirm: () => performGoogleSheetsDisconnect()
        });
        setShowConfirmModal(true);
    };

    const performGoogleSheetsDisconnect = async () => {

        setGoogleSheetsState(prev => ({ ...prev, isLoading: true }));

        try {
            const response = await api.delete('/integrations/google-sheets');

            if (response.data.success) {

                // Reset state immediately
                setGoogleSheetsState(prev => ({
                    ...prev,
                    isConnected: false,
                    connectionStatus: 'disconnected',
                    googleAccount: null,
                    availableSpreadsheets: [],
                    settings: {
                        ...prev.settings,
                        defaultSpreadsheetId: null,
                        defaultSpreadsheetName: null
                    }
                }));

                // Reload status to ensure consistency
                setTimeout(() => {
                    loadGoogleSheetsStatus();
                }, 500);

            } else {
                throw new Error(response.data.message || 'Failed to disconnect');
            }
        } catch (error) {
            console.error('❌ Error disconnecting Google Sheets:', error);
            setSuccessModalData({
                title: 'Disconnection Failed',
                message: 'Failed to disconnect Google Sheets. Please try again.',
                type: 'error',
                service: 'google_sheets'
            });
            setShowSuccessModal(true);
        } finally {
            setGoogleSheetsState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleSheetsSettingsUpdate = async (newSettings) => {
        try {
            const response = await api.patch('/integrations/google-sheets/settings', {
                sheetsSettings: newSettings
            });

            if (response.data.success) {
                setGoogleSheetsState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, ...newSettings }
                }));
            }
        } catch (error) {
            console.error('Error updating sheets settings:', error);
            setSuccessModalData({
                title: 'Update Failed',
                message: 'Failed to update sheets settings. Please try again.',
                type: 'error',
                service: 'google_sheets'
            });
            setShowSuccessModal(true);
        }
    };

    const handleCalendarSettingsUpdate = async (newSettings) => {
        try {
            const response = await api.patch('/integrations/google-calendar/settings', {
                calendarSettings: newSettings
            });

            if (response.data.success) {
                setGoogleCalendarState(prev => ({
                    ...prev,
                    settings: { ...prev.settings, ...newSettings }
                }));
            }
        } catch (error) {
            console.error('Error updating calendar settings:', error);
            setSuccessModalData({
                title: 'Update Failed',
                message: 'Failed to update calendar settings. Please try again.',
                type: 'error',
                service: 'google_calendar'
            });
            setShowSuccessModal(true);
        }
    };


    const handleIntegrationToggle = (type, field, value) => {
        onConfigChange({
            integrations: {
                ...integrations,
                [type]: {
                    ...integrations[type],
                    [field]: value
                }
            }
        });
    };

    // Conversion tracking (Google Ads + advanced custom snippet)
    const tracking = config.tracking || {};
    const googleAdsTracking = tracking.googleAds || { enabled: false, conversionId: '', conversionLabel: '' };

    const updateTracking = (updates) => {
        onConfigChange({ tracking: { ...tracking, ...updates } });
    };
    const updateGoogleAds = (field, value) => {
        onConfigChange({
            tracking: {
                ...tracking,
                googleAds: { ...googleAdsTracking, [field]: value }
            }
        });
    };

    const integrationOptions = [
        {
            id: 'googleCalendar',
            name: 'Google Calendar',
            description: 'Syncs booking slots and adds new appointments',
            imageUrl: 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/google-calendar-image.jpg',
            color: 'text-blue-500',
            bgColor: 'bg-blue-100',
            features: [
                'Automatic calendar event creation',
                'Send email invitations to customers',
                'Appointment reminders',
                'Conflict prevention'
            ],
            status: googleCalendarState.isConnected ? 'Connected' : 'Not Connected',
            connectionStatus: googleCalendarState.connectionStatus,
            config: { enabled: googleCalendarState.isConnected },
            realIntegration: true,
            googleAccount: googleCalendarState.googleAccount,
            isLoading: googleCalendarState.isLoading
        },
        {
            id: 'googleSheets',
            name: 'Google Sheets',
            description: 'Adds all new bookings to a Google Sheets file',
            imageUrl: 'https://d7zc54wk8qed4.cloudfront.net/assets/icons/google-sheets-image.jpg',
            color: 'text-green-500',
            bgColor: 'bg-green-100',
            features: [
                'Automatic data logging',
                'Customer information tracking',
                'Booking analytics',
                'Export capabilities'
            ],
            status: googleSheetsState.isConnected ? 'Connected' : 'Not Connected',
            connectionStatus: googleSheetsState.connectionStatus,
            config: { enabled: googleSheetsState.isConnected },
            realIntegration: true,
            googleAccount: googleSheetsState.googleAccount,
            isLoading: googleSheetsState.isLoading
        }
    ];

    const renderIntegrationCard = (integration) => (
        <div key={integration.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-200 shadow-sm hover:shadow-md">
            <div className="mb-6">
                {/* Top Section: Image and Switch */}
                <div className="flex items-start justify-between mb-3">
                    <div className={`w-20 h-20 rounded-xl ${integration.bgColor} flex items-center justify-center p-3 border border-white/20 shadow-lg`}>
                        <img
                            src={integration.imageUrl}
                            alt={`${integration.name} icon`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div
                            className="w-full h-full bg-gray-700 rounded-lg flex items-center justify-center hidden"
                        >
                            <span className="text-gray-400 text-lg font-bold">
                                {integration.name.charAt(0)}
                            </span>
                        </div>
                    </div>

                    {/* For real Google Calendar integration, show different controls */}
                    {integration.realIntegration ? (
                        <div className="flex items-center space-x-2">
                            {integration.isLoading && (
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            )}
                            {integration.config.enabled ? (
                                <button
                                    onClick={integration.id === 'googleCalendar' ? handleGoogleCalendarDisconnect : handleGoogleSheetsDisconnect}
                                    disabled={integration.isLoading}
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded disabled:opacity-50"
                                >
                                    Disconnect
                                </button>
                            ) : (
                                <button
                                    onClick={integration.id === 'googleCalendar' ? handleGoogleCalendarConnect : handleGoogleSheetsConnect}
                                    disabled={integration.isLoading}
                                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded disabled:opacity-50"
                                >
                                    Connect
                                </button>
                            )}
                        </div>
                    ) : (
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                            <input
                                type="checkbox"
                                checked={integration.config.enabled}
                                onChange={(e) => handleIntegrationToggle(integration.id, 'enabled', e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    )}
                </div>

                {/* Content Section: Title and Description aligned with image */}
                <div className="ml-0">
                    <h3 className="font-semibold text-white text-lg">{integration.name}</h3>
                    <p className="text-sm text-gray-400 mt-1">{integration.description}</p>
                    {/* Show connected account for Google Calendar */}
                    {integration.realIntegration && integration.googleAccount && (
                        <p className="text-xs text-blue-400 mt-1">
                            Connected as {integration.googleAccount.email}
                        </p>
                    )}
                </div>
            </div>

            {/* Status Badge */}
            <div className="mb-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${integration.config.enabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}>
                    {integration.config.enabled ? (
                        <>
                            <Icon name="check" size={12} className="mr-1" />
                            {integration.status}
                        </>
                    ) : (
                        <>
                            <Icon name="close" size={12} className="mr-1" />
                            {integration.status}
                        </>
                    )}
                </span>
            </div>

            {/* Features List */}
            <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-300 mb-2">Features:</h4>
                <ul className="space-y-1">
                    {integration.features.map((feature, index) => (
                        <li key={index} className="flex items-center space-x-2 text-xs text-gray-400">
                            <Icon name="check" size={12} className="text-green-400 flex-shrink-0" />
                            <span>{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Configuration Section */}
            {integration.config.enabled && (
                <div className="border-t border-gray-700 pt-4">
                    {integration.id === 'googleCalendar' && integration.realIntegration && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-300">Calendar Settings</h4>
                                <button
                                    onClick={() => setShowCalendarSettings(!showCalendarSettings)}
                                    className="text-xs text-blue-400 hover:text-blue-300"
                                >
                                    {showCalendarSettings ? 'Hide Settings' : 'Show Settings'}
                                </button>
                            </div>

                            {showCalendarSettings && (
                                <div className="space-y-3 bg-gray-700/30 p-3 rounded-lg">
                                    {/* Calendar Selection */}
                                    {googleCalendarState.availableCalendars.length > 0 && (
                                        <div>
                                            <label className="block text-xs text-gray-400 mb-1">Default Calendar</label>
                                            <select
                                                value={googleCalendarState.settings.defaultCalendarId}
                                                onChange={(e) => handleCalendarSettingsUpdate({ defaultCalendarId: e.target.value })}
                                                className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:border-blue-500 focus:outline-none"
                                            >
                                                {googleCalendarState.availableCalendars.map(cal => (
                                                    <option key={cal.id} value={cal.id}>
                                                        {cal.summary} {cal.primary ? '(Primary)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Event Settings */}
                                    <div className="space-y-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="create-events"
                                                checked={googleCalendarState.settings.createEvents}
                                                onChange={(e) => handleCalendarSettingsUpdate({ createEvents: e.target.checked })}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="create-events" className="text-xs text-gray-400">
                                                Automatically create calendar events
                                            </label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="send-invitations"
                                                checked={googleCalendarState.settings.sendInvitations}
                                                onChange={(e) => handleCalendarSettingsUpdate({ sendInvitations: e.target.checked })}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="send-invitations" className="text-xs text-gray-400">
                                                Send email invitations to customers
                                            </label>
                                        </div>

                                        <div>
                                            <label className="block text-xs text-gray-400 mb-1">Event Visibility</label>
                                            <select
                                                value={googleCalendarState.settings.eventVisibility}
                                                onChange={(e) => handleCalendarSettingsUpdate({ eventVisibility: e.target.value })}
                                                className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:border-blue-500 focus:outline-none"
                                            >
                                                <option value="default">Default</option>
                                                <option value="public">Public</option>
                                                <option value="private">Private</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}


                        </div>
                    )}

                    {integration.id === 'googleSheets' && integration.realIntegration && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-300">Sheets Settings</h4>
                                <button
                                    onClick={() => setShowSheetsSettings(!showSheetsSettings)}
                                    className="text-xs text-blue-400 hover:text-blue-300"
                                >
                                    {showSheetsSettings ? 'Hide Settings' : 'Show Settings'}
                                </button>
                            </div>

                            {showSheetsSettings && (
                                <div className="space-y-3 bg-gray-700/30 p-3 rounded-lg">
                                    {/* Spreadsheet Selection */}
                                    {googleSheetsState.availableSpreadsheets.length > 0 && (
                                        <div>
                                            <label className="block text-xs text-gray-400 mb-1">Default Spreadsheet</label>
                                            <select
                                                value={googleSheetsState.settings.defaultSpreadsheetId || ''}
                                                onChange={(e) => handleSheetsSettingsUpdate({
                                                    defaultSpreadsheetId: e.target.value,
                                                    defaultSpreadsheetName: e.target.options[e.target.selectedIndex].text
                                                })}
                                                className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:border-blue-500 focus:outline-none"
                                            >
                                                <option value="">Select a spreadsheet...</option>
                                                {googleSheetsState.availableSpreadsheets.map(sheet => (
                                                    <option key={sheet.id} value={sheet.id}>
                                                        {sheet.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Worksheet Name */}
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Worksheet Name</label>
                                        <input
                                            type="text"
                                            placeholder="Appointments"
                                            value={googleSheetsState.settings.worksheetName}
                                            onChange={(e) => handleSheetsSettingsUpdate({ worksheetName: e.target.value })}
                                            className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:border-blue-500 focus:outline-none"
                                        />
                                    </div>

                                    {/* Sync Settings */}
                                    <div className="space-y-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="sync-new-appointments"
                                                checked={googleSheetsState.settings.syncNewAppointments}
                                                onChange={(e) => handleSheetsSettingsUpdate({ syncNewAppointments: e.target.checked })}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="sync-new-appointments" className="text-xs text-gray-400">
                                                Automatically sync new appointments
                                            </label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="auto-create-spreadsheet"
                                                checked={googleSheetsState.settings.autoCreateSpreadsheet}
                                                onChange={(e) => handleSheetsSettingsUpdate({ autoCreateSpreadsheet: e.target.checked })}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="auto-create-spreadsheet" className="text-xs text-gray-400">
                                                Auto-create spreadsheet if none selected
                                            </label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="include-customer-info"
                                                checked={googleSheetsState.settings.includeCustomerInfo}
                                                onChange={(e) => handleSheetsSettingsUpdate({ includeCustomerInfo: e.target.checked })}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="include-customer-info" className="text-xs text-gray-400">
                                                Include customer contact information
                                            </label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="include-service-details"
                                                checked={googleSheetsState.settings.includeServiceDetails}
                                                onChange={(e) => handleSheetsSettingsUpdate({ includeServiceDetails: e.target.checked })}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="include-service-details" className="text-xs text-gray-400">
                                                Include service details and pricing
                                            </label>
                                        </div>
                                    </div>

                                    {/* Date and Time Format */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-400 mb-1">Date Format</label>
                                            <select
                                                value={googleSheetsState.settings.dateFormat}
                                                onChange={(e) => handleSheetsSettingsUpdate({ dateFormat: e.target.value })}
                                                className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:border-blue-500 focus:outline-none"
                                            >
                                                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-400 mb-1">Time Format</label>
                                            <select
                                                value={googleSheetsState.settings.timeFormat}
                                                onChange={(e) => handleSheetsSettingsUpdate({ timeFormat: e.target.value })}
                                                className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:border-blue-500 focus:outline-none"
                                            >
                                                <option value="12">12 Hour</option>
                                                <option value="24">24 Hour</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            )}
        </div>
    );

    return (
        <div className="p-4 h-full overflow-y-auto">
            <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-semibold mb-2">Integrations</h3>
                <p className="text-sm text-gray-400 mb-8">
                    Connect your appointment bookings with external services and tools
                </p>

                <div className="space-y-6">
                    {integrationOptions.map(renderIntegrationCard)}

                    {/* Conversion Tracking (Google Ads) */}
                    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-200 shadow-sm">
                        <div className="flex items-start justify-between mb-3">
                            <div className="w-20 h-20 rounded-xl bg-yellow-100 flex items-center justify-center p-3 border border-white/20 shadow-lg">
                                <img
                                    src="https://d7zc54wk8qed4.cloudfront.net/assets/icons/google-ads-image.jpg"
                                    alt="Google Ads icon"
                                    className="w-full h-full object-contain"
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                                <div className="w-full h-full bg-gray-700 rounded-lg flex items-center justify-center hidden">
                                    <span className="text-gray-400 text-lg font-bold">A</span>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                                <input
                                    type="checkbox"
                                    checked={googleAdsTracking.enabled || false}
                                    onChange={(e) => updateGoogleAds('enabled', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        <div className="mb-4">
                            <h3 className="font-semibold text-white text-lg">Google Ads Conversion Tracking</h3>
                            <p className="text-sm text-gray-400 mt-1">Fire a conversion when a booking is confirmed, so you can measure ad performance and ROAS.</p>
                        </div>

                        {googleAdsTracking.enabled && (
                            <div className="border-t border-gray-700 pt-4 space-y-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Conversion ID</label>
                                    <input
                                        type="text"
                                        placeholder="AW-123456789"
                                        value={googleAdsTracking.conversionId || ''}
                                        onChange={(e) => updateGoogleAds('conversionId', e.target.value.trim())}
                                        className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Conversion Label</label>
                                    <input
                                        type="text"
                                        placeholder="AbC-D_efGhIjK"
                                        value={googleAdsTracking.conversionLabel || ''}
                                        onChange={(e) => updateGoogleAds('conversionLabel', e.target.value.trim())}
                                        className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3">
                                    <div className="flex items-start space-x-2">
                                        <Icon name="info" size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-blue-200 leading-relaxed">
                                            In Google Ads, open <strong>Goals → Conversions → your conversion action → Tag setup → Use Google tag</strong>. Copy the <strong>Conversion ID</strong> (<code className="text-blue-300">AW-XXXXXXXXX</code>) and <strong>Conversion label</strong> from the event snippet&apos;s <code className="text-blue-300">send_to</code> value.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Advanced: custom snippet */}
                        <div className="border-t border-gray-700 pt-4 mt-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-300">Advanced: Custom tracking code</h4>
                                <button
                                    onClick={() => setShowAdvancedTracking(!showAdvancedTracking)}
                                    className="text-xs text-blue-400 hover:text-blue-300"
                                >
                                    {showAdvancedTracking ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            {showAdvancedTracking && (
                                <div className="mt-3">
                                    <p className="text-xs text-gray-400 mb-2">
                                        Optional JavaScript run once when a booking succeeds — for Meta Pixel, GA4 events, or other platforms. Paste JS only (no <code className="text-gray-300">&lt;script&gt;</code> tags).
                                    </p>
                                    <textarea
                                        rows={5}
                                        placeholder={"fbq('track', 'Lead');\ngtag('event', 'generate_lead');"}
                                        value={tracking.customCode || ''}
                                        onChange={(e) => updateTracking({ customCode: e.target.value })}
                                        className="w-full bg-gray-700 text-white px-3 py-2 rounded text-xs font-mono border border-gray-600 focus:border-blue-500 focus:outline-none"
                                    />
                                    <p className="text-xs text-yellow-300/80 mt-2">
                                        This code runs on your site exactly as written. Only paste code you trust.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Integration Benefits */}
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <h4 className="text-sm font-medium text-gray-300 mb-3">🚀 Why Use Integrations?</h4>
                        <div className="grid grid-cols-1 gap-3">
                            <div className="flex items-start space-x-2">
                                <Icon name="check" size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-white font-medium">Automated Workflows</p>
                                    <p className="text-xs text-gray-400">Reduce manual work with automatic data syncing</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-2">
                                <Icon name="check" size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-white font-medium">Better Organization</p>
                                    <p className="text-xs text-gray-400">Keep all your appointments organized in one place</p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-2">
                                <Icon name="check" size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-white font-medium">Enhanced Analytics</p>
                                    <p className="text-xs text-gray-400">Track booking patterns and business insights</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Getting Started Guide */}
                    {!googleCalendar.enabled && !googleSheets.enabled && (
                        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                                <Icon name="info" size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h4 className="text-sm font-medium text-blue-300 mb-2">Getting Started</h4>
                                    <p className="text-xs text-blue-200 mb-3">
                                        Start by enabling one integration above. Each integration helps streamline your appointment management in different ways:
                                    </p>
                                    <ul className="space-y-1 text-xs text-blue-200">
                                        <li>• <strong>Google Calendar:</strong> Perfect for scheduling and avoiding conflicts</li>
                                        <li>• <strong>Google Sheets:</strong> Great for tracking and analyzing bookings</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* OAuth Result Modal */}
                <OAuthResultModal
                    isOpen={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                    type={successModalData?.type}
                    title={successModalData?.title}
                    message={successModalData?.message}
                    service={successModalData?.service}
                    accountInfo={successModalData?.accountInfo}
                    onRetry={successModalData?.type === 'error' ?
                        (successModalData?.service === 'google_sheets' ? handleGoogleSheetsConnect : handleGoogleCalendarConnect)
                        : null}
                />

                {/* Confirmation Modal */}
                <ConfirmationModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={confirmModalData?.onConfirm}
                    title={confirmModalData?.title}
                    message={confirmModalData?.message}
                    confirmText={confirmModalData?.confirmText}
                    type={confirmModalData?.type}
                />
            </div>
        </div>
    );
};

export default AppointmentIntegrations;
